import React, { useState, useEffect } from 'react';
import { toWebpImageUrl } from '../utils/imageUrl';
import { useNavigate } from 'react-router-dom';
import {
  DataTable,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Button,
  Grid,
  Column,
  Tile,
  Tag,
  Dropdown,
  Modal,
  InlineLoading,
  Pagination,
  Search,
} from '@carbon/react';
import {
  View,
  ShoppingCart,
  Package,
  Calendar,
  DollarSign,
  CheckmarkFilled,
  WarningAltFilled,
  Information,
  Close,
} from '@carbon/icons-react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { orderService } from '../services/orderService';
import { supabase } from '../services/supabaseClient';

const OrdersPage = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { user, isAuthenticated } = useAuth();
  const { addToCart, clearCart } = useCart();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const isDark = theme === 'dark';
  const bgPrimary = isDark ? '#161616' : '#ffffff';
  const bgSecondary = isDark ? '#262626' : '#f4f4f4';
  const textPrimary = isDark ? '#f4f4f4' : '#161616';
  const textSecondary = isDark ? '#c6c6c6' : '#525252';
  const borderColor = isDark ? '#393939' : '#e0e0e0';

  // Fetch orders
  useEffect(() => {
    const fetchOrders = async () => {
      if (!isAuthenticated || !user) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const userOrders = await orderService.getUserOrders(user.id);
        setOrders(userOrders);
      } catch (err) {
        console.error('Error fetching orders:', err);
        setError(err.message || 'Failed to load orders');
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [isAuthenticated, user]);

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'orange';
      case 'processing': return 'blue';
      case 'shipped': return 'teal';
      case 'delivered': return 'green';
      case 'cancelled': return 'red';
      default: return 'gray';
    }
  };

  // Get status text
  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return 'Pending';
      case 'processing': return 'Processing';
      case 'shipped': return 'Shipped';
      case 'delivered': return 'Delivered';
      case 'cancelled': return 'Cancelled';
      default: return 'Unknown';
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Filter and search orders
  const filteredOrders = orders.filter(order => {
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    const matchesSearch = searchTerm === '' || 
      order.orderNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (order.shippingInfo?.firstName && order.shippingInfo.firstName.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (order.shippingInfo?.lastName && order.shippingInfo.lastName.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesStatus && matchesSearch;
  });

  // Pagination
  const totalPages = Math.ceil(filteredOrders.length / pageSize);
  const startIndex = (page - 1) * pageSize;
  const paginatedOrders = filteredOrders.slice(startIndex, startIndex + pageSize);

  // Reset to page 1 when search term or status filter changes
  useEffect(() => {
    setPage(1);
  }, [searchTerm, statusFilter]);

  // Handle view order
  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setIsModalOpen(true);
  };

  // Handle reorder - always fetch fresh product data to ensure correct images
  const handleReorder = async (order) => {
    try {
      clearCart();
      
      // Process each item and always fetch fresh product data (like ShopPage does)
      for (const item of order.items) {
        let itemImage = item.image_url || item.image;
        let productData = null;
        
        // Always try to fetch fresh product data to get current image URL
        if (item.id) {
          try {
            const { data: product, error } = await supabase
              .from('products')
              .select('image_url, category')
              .eq('id', item.id)
              .maybeSingle();
            
            if (error) {
              console.warn('Error fetching product by ID:', error);
            } else if (product) {
              productData = product;
              itemImage = product.image_url; // Products table only has image_url, not image
            }
          } catch (err) {
            console.warn('Could not fetch product by ID:', err);
          }
        }
        
        // If still no image, try to find by name
        if (!itemImage && item.name) {
          try {
            // Clean the name for better matching (remove special characters, extra spaces)
            const cleanName = item.name.trim().replace(/\s+/g, ' ');
            const { data: products, error } = await supabase
              .from('products')
              .select('image_url, category')
              .ilike('name', `%${cleanName}%`)
              .limit(1);
            
            if (error) {
              console.warn('Error fetching product by name:', error);
            } else if (products && products.length > 0) {
              productData = products[0];
              itemImage = products[0].image_url; // Products table only has image_url, not image
            }
          } catch (err) {
            console.warn('Could not fetch product by name:', err);
          }
        }
        
        // Add items to cart matching ShopPage format exactly
        for (let i = 0; i < item.quantity; i++) {
          addToCart({
            id: item.id || item.name,
            name: item.name,
            price: item.price,
            image: toWebpImageUrl(itemImage), // Use fresh image from products table (matching ShopPage behavior)
            category: productData?.category, // Include category like ShopPage does
          });
        }
      }
      
      setIsModalOpen(false);
      navigate('/cart');
    } catch (error) {
      console.error('Error reordering items:', error);
    }
  };

  // Table headers
  const headers = [
    { key: 'orderNumber', header: 'Order #' },
    { key: 'date', header: 'Date' },
    { key: 'status', header: 'Status' },
    { key: 'items', header: 'Items' },
    { key: 'total', header: 'Total' },
    { key: 'actions', header: 'Actions' },
  ];

  // Table rows
  const rows = paginatedOrders.map(order => ({
    id: order.id,
    orderNumber: order.orderNumber,
    date: formatDate(order.createdAt),
    status: order.status,
    items: order.items?.length || 0,
    total: `$${order.total.toFixed(2)}`,
    actions: order,
  }));

  if (loading) {
    return (
      <div style={{ backgroundColor: bgPrimary, minHeight: '100vh', padding: '48px 0' }}>
        <Grid>
          <Column lg={16} md={8} sm={4}>
            <Tile style={{ backgroundColor: bgSecondary, padding: '48px', textAlign: 'center' }}>
              <InlineLoading description="Loading your orders..." />
            </Tile>
          </Column>
        </Grid>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ backgroundColor: bgPrimary, minHeight: '100vh', padding: '48px 0' }}>
        <Grid>
          <Column lg={16} md={8} sm={4}>
            <Tile style={{ backgroundColor: bgSecondary, padding: '24px' }}>
              <InlineNotification
                kind="error"
                title="Error"
                subtitle={error}
                onClose={() => setError(null)}
              />
            </Tile>
          </Column>
        </Grid>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: bgPrimary, minHeight: '100vh', padding: '48px 0' }}>
      <Grid>
        <Column lg={16} md={8} sm={4}>
          {/* Header */}
          <div style={{ marginBottom: '32px' }}>
            <h1 style={{ color: textPrimary, fontSize: '2rem', fontWeight: 600, marginBottom: '8px' }}>
              My Orders
            </h1>
            <p style={{ color: textSecondary, fontSize: '0.875rem' }}>
              View and track your order history
            </p>
          </div>

          {orders.length === 0 ? (
            <Tile style={{ backgroundColor: bgSecondary, padding: '48px', textAlign: 'center' }}>
              <Package size={64} style={{ color: textSecondary, marginBottom: '16px' }} />
              <h2 style={{ color: textPrimary, marginBottom: '8px' }}>No Orders Yet</h2>
              <p style={{ color: textSecondary, marginBottom: '24px' }}>
                You haven't placed any orders yet. Start shopping to see your orders here.
              </p>
              <Button onClick={() => navigate('/shop')} renderIcon={ShoppingCart}>
                Start Shopping
              </Button>
            </Tile>
          ) : (
            <>
              {/* Filters and Search */}
              <Tile style={{ backgroundColor: bgSecondary, padding: '16px', marginBottom: '16px' }}>
                <Grid style={{ alignItems: 'flex-end' }}>
                  <Column lg={8} md={4} sm={4}>
                    <Search
                      labelText="Search orders"
                      placeholder="Search by order number or name..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </Column>
                  <Column lg={8} md={4} sm={4}>
                    <Dropdown
                      id="status-filter"
                      titleText="Filter by Status"
                      label="Select status"
                      items={['All Orders', 'Pending', 'Processing', 'Shipped', 'Delivered', 'Cancelled']}
                      selectedItem={statusFilter === 'all' ? 'All Orders' : statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)}
                      onChange={({ selectedItem }) => {
                        if (selectedItem) {
                          // Map display text back to status value
                          const statusMap = {
                            'All Orders': 'all',
                            'Pending': 'pending',
                            'Processing': 'processing',
                            'Shipped': 'shipped',
                            'Delivered': 'delivered',
                            'Cancelled': 'cancelled',
                          };
                          setStatusFilter(statusMap[selectedItem] || 'all');
                        }
                      }}
                    />
                  </Column>
                </Grid>
              </Tile>

              {/* Orders Table */}
              <Tile style={{ backgroundColor: bgSecondary, padding: 0, overflow: 'hidden' }}>
                <DataTable rows={rows} headers={headers} isSortable>
                  {({ rows, headers, getTableProps, getHeaderProps, getRowProps }) => (
                    <Table {...getTableProps()}>
                      <TableHead>
                        <TableRow>
                          {headers.map((header) => {
                            const { key, ...headerProps } = getHeaderProps({ header });
                            return (
                              <TableHeader key={key} {...headerProps}>
                                {header.header}
                              </TableHeader>
                            );
                          })}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rows.map((row) => {
                          const order = row.cells.find(cell => cell.info.header === 'actions')?.value;
                          const { key, ...rowProps } = getRowProps({ row });
                          return (
                            <TableRow key={key} {...rowProps}>
                              {row.cells.map((cell) => {
                                if (cell.info.header === 'orderNumber') {
                                  return (
                                    <TableCell key={cell.id}>
                                      <span style={{ fontFamily: 'monospace', fontWeight: 500 }}>
                                        {cell.value}
                                      </span>
                                    </TableCell>
                                  );
                                }
                                if (cell.info.header === 'status') {
                                  return (
                                    <TableCell key={cell.id}>
                                      <Tag type={getStatusColor(cell.value)}>
                                        {getStatusText(cell.value)}
                                      </Tag>
                                    </TableCell>
                                  );
                                }
                                if (cell.info.header === 'total') {
                                  return (
                                    <TableCell key={cell.id}>
                                      <span style={{ color: '#24a148', fontWeight: 600, fontSize: '1rem' }}>
                                        {cell.value}
                                      </span>
                                    </TableCell>
                                  );
                                }
                                if (cell.info.header === 'actions') {
                                  return (
                                    <TableCell key={cell.id}>
                                      <div style={{ display: 'flex', gap: '8px' }}>
                                        <Button
                                          kind="ghost"
                                          size="sm"
                                          renderIcon={View}
                                          onClick={() => handleViewOrder(order)}
                                          hasIconOnly
                                          iconDescription="View order"
                                        />
                                        <Button
                                          kind="ghost"
                                          size="sm"
                                          renderIcon={ShoppingCart}
                                          onClick={() => handleReorder(order)}
                                          hasIconOnly
                                          iconDescription="Reorder"
                                        />
                                      </div>
                                    </TableCell>
                                  );
                                }
                                return <TableCell key={cell.id}>{cell.value}</TableCell>;
                              })}
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}
                </DataTable>

                {/* Pagination */}
                {filteredOrders.length > 0 && (
                  <div style={{ padding: '16px', borderTop: `1px solid ${borderColor}` }}>
                    <Pagination
                      backwardText="Previous"
                      forwardText="Next"
                      itemsPerPageText="Items per page:"
                      page={page}
                      pageNumberText="Page Number"
                      pageSize={pageSize}
                      pageSizes={[10, 20, 30, 40, 50]}
                      size="md"
                      totalItems={filteredOrders.length}
                      onChange={({ page, pageSize }) => {
                        setPage(page);
                        setPageSize(pageSize);
                      }}
                    />
                  </div>
                )}
              </Tile>
            </>
          )}

          {/* Order Details Modal */}
          <Modal
            open={isModalOpen}
            modalHeading="Order Details"
            primaryButtonText="Reorder Items"
            secondaryButtonText="Close"
            onRequestClose={() => setIsModalOpen(false)}
            onRequestSubmit={() => {
              if (selectedOrder) handleReorder(selectedOrder);
            }}
            size="lg"
          >
            {selectedOrder && (
              <div style={{ padding: '16px 0' }}>
                {/* Order Summary */}
                <Grid style={{ marginBottom: '24px' }}>
                  <Column lg={8} md={4} sm={4}>
                    <div style={{ marginBottom: '16px' }}>
                      <p style={{ color: textSecondary, fontSize: '0.75rem', marginBottom: '4px' }}>
                        ORDER NUMBER
                      </p>
                      <p style={{ color: textPrimary, fontSize: '1rem', fontFamily: 'monospace', fontWeight: 500 }}>
                        {selectedOrder.orderNumber}
                      </p>
                    </div>
                  </Column>
                  <Column lg={8} md={4} sm={4}>
                    <div style={{ marginBottom: '16px' }}>
                      <p style={{ color: textSecondary, fontSize: '0.75rem', marginBottom: '4px' }}>
                        STATUS
                      </p>
                      <Tag type={getStatusColor(selectedOrder.status)}>
                        {getStatusText(selectedOrder.status)}
                      </Tag>
                    </div>
                  </Column>
                  <Column lg={8} md={4} sm={4}>
                    <div style={{ marginBottom: '16px' }}>
                      <p style={{ color: textSecondary, fontSize: '0.75rem', marginBottom: '4px' }}>
                        DATE
                      </p>
                      <p style={{ color: textPrimary, fontSize: '1rem' }}>
                        {formatDate(selectedOrder.createdAt)}
                      </p>
                    </div>
                  </Column>
                  <Column lg={8} md={4} sm={4}>
                    <div style={{ marginBottom: '16px' }}>
                      <p style={{ color: textSecondary, fontSize: '0.75rem', marginBottom: '4px' }}>
                        TOTAL
                      </p>
                      <p style={{ color: '#24a148', fontSize: '1.5rem', fontWeight: 600 }}>
                        ${selectedOrder.total.toFixed(2)}
                      </p>
                    </div>
                  </Column>
                </Grid>

                {/* Shipping Information */}
                {selectedOrder.shippingInfo && (
                  <div style={{ marginBottom: '24px', padding: '16px', backgroundColor: bgPrimary, borderRadius: '4px' }}>
                    <h3 style={{ color: textPrimary, fontSize: '1rem', fontWeight: 600, marginBottom: '12px' }}>
                      Shipping Address
                    </h3>
                    <p style={{ color: textPrimary, fontSize: '0.875rem', lineHeight: 1.6, margin: 0 }}>
                      {selectedOrder.shippingInfo.firstName} {selectedOrder.shippingInfo.lastName}
                      <br />
                      {selectedOrder.shippingInfo.address}
                      <br />
                      {selectedOrder.shippingInfo.city}, {selectedOrder.shippingInfo.state} {selectedOrder.shippingInfo.zipCode}
                      {selectedOrder.shippingInfo.country && (
                        <>
                          <br />
                          {selectedOrder.shippingInfo.country}
                        </>
                      )}
                    </p>
                  </div>
                )}

                {/* Tracking Number */}
                {selectedOrder.trackingNumber && (
                  <div style={{ marginBottom: '24px', padding: '16px', backgroundColor: bgPrimary, borderRadius: '4px' }}>
                    <h3 style={{ color: textPrimary, fontSize: '1rem', fontWeight: 600, marginBottom: '8px' }}>
                      Tracking Number
                    </h3>
                    <p style={{ color: textPrimary, fontSize: '0.875rem', fontFamily: 'monospace', fontWeight: 500, margin: 0 }}>
                      {selectedOrder.trackingNumber}
                    </p>
                  </div>
                )}

                {/* Shipment Photo */}
                {selectedOrder.shipmentPhoto && (
                  <div style={{ marginBottom: '24px' }}>
                    <h3 style={{ color: textPrimary, fontSize: '1rem', fontWeight: 600, marginBottom: '12px' }}>
                      Shipment Photo
                    </h3>
                    <img
                      src={selectedOrder.shipmentPhoto}
                      alt="Shipment"
                      style={{
                        width: '100%',
                        maxWidth: '400px',
                        height: 'auto',
                        borderRadius: '4px',
                        border: `1px solid ${borderColor}`,
                      }}
                    />
                  </div>
                )}

                {/* Order Items */}
                <div>
                  <h3 style={{ color: textPrimary, fontSize: '1rem', fontWeight: 600, marginBottom: '16px' }}>
                    Order Items ({selectedOrder.items?.length || 0})
                  </h3>
                  {selectedOrder.items && selectedOrder.items.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {selectedOrder.items.map((item, idx) => (
                        <div
                          key={idx}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '12px',
                            backgroundColor: bgPrimary,
                            borderRadius: '4px',
                            border: `1px solid ${borderColor}`,
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <p style={{ color: textPrimary, fontSize: '0.875rem', fontWeight: 500, marginBottom: '4px' }}>
                              {item.name}
                            </p>
                            <p style={{ color: textSecondary, fontSize: '0.75rem', margin: 0 }}>
                              Qty: {item.quantity} × ${item.price.toFixed(2)}
                            </p>
                          </div>
                          <div style={{ color: '#24a148', fontSize: '1rem', fontWeight: 600 }}>
                            ${(item.price * item.quantity).toFixed(2)}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p style={{ color: textSecondary, fontSize: '0.875rem', fontStyle: 'italic' }}>
                      No items found for this order.
                    </p>
                  )}
                </div>
              </div>
            )}
          </Modal>
        </Column>
      </Grid>
    </div>
  );
};

export default OrdersPage;

